import React, { useCallback, useEffect, useMemo, useState } from "react";
import { Badge, Button } from "react-bootstrap";
import { resumeWhatsAppCampaign } from "../api/whatsapp.js";
import { SelectColumnFilter } from "./Table.js";
import WhatsAppReportTable from "./WhatsAppReportTable.js";
import { formatWhatsAppReportDate as dateLabel, whatsAppReportName } from "../utils/whatsappReportPresentation.js";
import { loadWhatsAppMessagesReport } from "../utils/whatsappReports.js";
import { WHATSAPP_REGISTRATION_USER_TYPES } from "../utils/whatsappRegistration.js";

const STATUSES = {
  pending: "Na fila", reserved: "Preparando", submitting: "Enviando", accepted: "Aceito pela Meta",
  delivered: "Entregue", read: "Lido", failed: "Falhou", unknown: "Resultado não confirmado",
  suppressed: "Não enviado", cancelled: "Cancelado", paused: "Pausado",
};
const ERRORS = {
  WHATSAPP_REGISTRATION_DISABLED: "Envio automático desativado ou configuração alterada",
  WHATSAPP_TEMPLATE_VARIABLE_MISSING: "Faltam dados para preencher o modelo",
  WHATSAPP_REGISTRATION_TEMPLATE_UNAVAILABLE: "Envio cancelado: o modelo selecionado não está disponível ou aprovado",
  WHATSAPP_TEMPLATE_UNAVAILABLE: "Envio cancelado: a versão selecionada do modelo não está disponível",
  WHATSAPP_TEMPLATE_NOT_APPROVED: "Envio cancelado: o modelo selecionado não está aprovado para envio",
  WHATSAPP_TEMPLATE_BINDINGS_INVALID: "Envio cancelado: os campos do modelo não correspondem ao formulário",
  PHONE_DUPLICATE: "Telefone compartilhado com outro usuário",
  RECIPIENT_CHANGED_OR_INACTIVE: "Cadastro inativo ou destinatário alterado",
  PHONE_CHANGED_OR_INVALID: "Telefone inválido ou alterado",
  RECIPIENT_SUPPRESSED: "Destinatário bloqueado para envio",
};
const typeLabel = (type) => type === null || type === undefined ? "—" : WHATSAPP_REGISTRATION_USER_TYPES.find((entry) => entry.type === Number(type))?.label || "—";

export default function WhatsAppRegistrationHistoryCard({
  eventId, eventTimezone = "America/Sao_Paulo", eventTitle, refreshKey, onInspect,
  campaigns, campaignsLoading, campaignsError, onLoadState, onCreate, createDisabled,
}) {
  const [messages, setMessages] = useState([]);
  const [loadedEventId, setLoadedEventId] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [reload, setReload] = useState(0);
  const [resuming, setResuming] = useState(null);
  const campaignKey = JSON.stringify(campaigns.map(({ campaign_id, name, template_id, template_title, template_version }) => ({ campaign_id, name, template_id, template_title, template_version })));
  const reportCampaigns = useMemo(() => JSON.parse(campaignKey), [campaignKey]);
  useEffect(() => {
    onLoadState?.(true);
    if (campaignsLoading) return undefined;
    if (campaignsError) { onLoadState?.(false); return undefined; }
    const controller = new AbortController();
    setLoading(true); setError("");
    loadWhatsAppMessagesReport({ eventId, eventTimezone, campaigns: reportCampaigns, signal: controller.signal })
      .then((rows) => {
        if (!controller.signal.aborted) { setMessages(rows); setLoadedEventId(eventId); }
      })
      .catch((requestError) => {
        if (!controller.signal.aborted) setError(requestError.message || "Não foi possível carregar as mensagens. Atualize para tentar novamente.");
      })
      .finally(() => { if (!controller.signal.aborted) { setLoading(false); onLoadState?.(false); } });
    return () => controller.abort();
  }, [reportCampaigns, campaignsError, campaignsLoading, eventId, eventTimezone, refreshKey, reload, onLoadState]);
  const resume = useCallback(async (row) => {
    setResuming(row.campaign_id); setError("");
    try { await resumeWhatsAppCampaign({ eventId, campaignId: row.campaign_id }); setReload((previous) => previous + 1); }
    catch (_) { setError("Não foi possível retomar. Revise o remetente, a disponibilidade do modelo e a configuração do envio automático."); }
    finally { setResuming(null); }
  }, [eventId]);
  const columns = useMemo(() => [
    { Header: "Usuário", accessor: (row) => whatsAppReportName(row.name, row.user_id, "Participante"), id: "name" },
    { Header: "Telefone", accessor: (row) => row.masked_phone || "Telefone oculto", id: "masked_phone" },
    { Header: "Modelo", accessor: (row) => whatsAppReportName(row.template_title, row.template_id, "Modelo"), id: "template_title", Filter: SelectColumnFilter, filter: "equals" },
    { Header: "Origem", accessor: "origin", Filter: SelectColumnFilter, filter: "equals" },
    { Header: "Status", accessor: "status", Filter: SelectColumnFilter, filter: "equals", renderFilterLabel: (value) => STATUSES[value] || value,
      exportValue: (row) => STATUSES[row.status] || row.status,
      Cell: ({ value }) => <Badge bg={["delivered", "read"].includes(value) ? "success" : ["failed", "unknown"].includes(value) ? "warning" : "secondary"}>{STATUSES[value] || value}</Badge> },
    { Header: "Data", accessor: (row) => dateLabel(row.report_date, eventTimezone), id: "report_date" },
    { Header: "", id: "icons", disableExport: true, disableFilters: true, disableSortBy: true,
      Cell: ({ row }) => <div className="d-flex gap-1 text-nowrap">
        <Button size="sm" variant="outline-secondary" onClick={() => onInspect(row.original)}>Detalhes</Button>
        {row.original.status === "paused" && <Button size="sm" variant="outline-primary" disabled={resuming !== null} onClick={() => resume(row.original)}>{resuming === row.original.campaign_id ? "Retomando…" : "Retomar"}</Button>}
      </div> },
  ], [eventTimezone, onInspect, resume, resuming]);
  const extraColumns = useMemo(() => [
    { Header: "ID da mensagem", accessor: "delivery_id" },
    { Header: "ID do usuário", accessor: "user_id" },
    { Header: "Campanha", accessor: (row) => row.origin === "Campanha" ? whatsAppReportName(row.campaign_name, row.campaign_id, "Campanha") : "—", id: "campaign_name" },
    { Header: "E-mail (após cadastro)", accessor: "email" },
    { Header: "Tipo", accessor: (row) => typeLabel(row.user_type), id: "user_type", Filter: SelectColumnFilter, filter: "equals" },
    { Header: "Versão", accessor: "template_version" },
    { Header: "Tentativas", accessor: "attempt_count" },
    { Header: "Enviado em", accessor: (row) => dateLabel(row.submitted_at, eventTimezone), id: "submitted_at" },
    { Header: "Entregue em", accessor: (row) => dateLabel(row.delivered_at, eventTimezone), id: "delivered_at" },
    { Header: "Lido em", accessor: (row) => dateLabel(row.read_at, eventTimezone), id: "read_at" },
    { Header: "Criado em", accessor: (row) => dateLabel(row.created_at, eventTimezone), id: "created_at" },
    { Header: "Atualizado em", accessor: (row) => dateLabel(row.updated_at, eventTimezone), id: "updated_at" },
    { Header: "ID na Meta", accessor: "provider_message_id" },
    { Header: "Observação", accessor: (row) => [ERRORS[row.error_code] || row.error_code, row.error_detail].filter(Boolean).join(" · "), id: "observation" },
  ], [eventTimezone]);
  return <WhatsAppReportTable key={eventId} title="Todas as mensagens"
    description="Consulte o status individual dos envios após cadastro e das mensagens de campanhas"
    filename={`${eventTitle || eventId} - WhatsApp - mensagens`} eventTimezone={eventTimezone}
    data={loadedEventId === eventId ? messages : []} columns={columns} extraColumns={extraColumns}
    dateAccessor="report_date" statusOptions={STATUSES} loading={!campaignsError && (loading || campaignsLoading)}
    error={campaignsError || error} onCreate={onCreate} createLabel="Enviar mensagem individual" createDisabled={createDisabled} />;
}
